import React, { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@pisaka/ui';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { Sparkles, PenLine, ALargeSmall, CircleHelp, ScrollText, Shield, RefreshCcw, Tag, CreditCard, BookOpen, ScanText } from 'lucide-react';
import { AppStateTypes } from '../../../store/appStateTypes';
import {
  editorPageUrl, faqPageUrl, fontCreatorPageUrl, offerPageUrl, privacyPageUrl, refundPageUrl, termsPageUrl, aiPageUrl, oldBasePageUrl, pricingPageUrl, paymentPageUrl, flashcardsPageUrl, recognizePageUrl
} from '../../../helpers/urls';

type PropsType = {
  isOpen: boolean;
  onClose: () => void;
  AuthContent?: ReactNode;
  StatsContent?: ReactNode;
};

const NavItem: FC<{ href: string; icon: React.ReactNode; label: string; onClick: () => void; bold?: boolean; active?: boolean; badge?: string }> = ({ href, icon, label, onClick, bold, active, badge }) => (
  <a
    href={href}
    onClick={onClick}
    className={`flex items-center gap-3 py-3 px-4 border-b border-gray-100 text-sm transition-colors ${bold ? 'font-medium' : ''} ${active ? 'bg-gray-100 text-gray-900' : 'hover:bg-gray-50'}`}
  >
    <span className={`shrink-0 ${active ? 'text-gray-700' : 'text-gray-600'}`}>{icon}</span>
    {label}
    {badge && (
      <span className="ml-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white uppercase">
        {badge}
      </span>
    )}
  </a>
);


export const SidebarComponent: FC<PropsType> = ({ isOpen, onClose, AuthContent, StatsContent }) => {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { user } = useSelector((state: AppStateTypes) => state.auth) || {};
  const isEn = i18n.language === 'en';

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="left" className="w-[280px] flex flex-col justify-between p-0">
        <div>
          <SheetHeader className="p-4 border-b">
            <SheetTitle className="text-left">
              {AuthContent}
            </SheetTitle>
          </SheetHeader>
          <nav className="flex flex-col">
            <NavItem href={editorPageUrl} icon={<PenLine className="w-4 h-4" />} label={t('sidebar.noteGenerator')} onClick={onClose} active={pathname === '/editor/'} />
            <NavItem href={fontCreatorPageUrl} icon={<ALargeSmall className="w-4 h-4" />} label={t('sidebar.fontGenerator')} onClick={onClose} active={pathname.startsWith('/font-creator')} />
            <NavItem href={aiPageUrl} icon={<Sparkles className="w-4 h-4" />} label={t('sidebar.aiChat')} onClick={onClose} bold active={pathname.startsWith('/ai')} badge="new" />
            <NavItem href={recognizePageUrl} icon={<ScanText className="w-4 h-4" />} label="Распознавание текста" onClick={onClose} bold active={pathname.startsWith('/recognize') || pathname.startsWith('/photo-to-text') || pathname.startsWith('/audio-to-text') || pathname.startsWith('/youtube-to-text') || pathname.startsWith('/pdf-to-text')} badge="new" />
            {Number(user?.id) === 1 && <NavItem href={flashcardsPageUrl} icon={<BookOpen className="w-4 h-4" />} label="Мои материалы" onClick={onClose} bold active={pathname.startsWith('/materials')} badge="new" />}
            <NavItem href={pricingPageUrl} icon={<Tag className="w-4 h-4" />} label={t('sidebar.pricing')} onClick={onClose} active={pathname.startsWith('/pricing')} />
            <NavItem href={faqPageUrl} icon={<CircleHelp className="w-4 h-4" />} label={t('sidebar.faq')} onClick={onClose} active={pathname.startsWith('/faq')} />
            {!isEn && <NavItem href={offerPageUrl} icon={<ScrollText className="w-4 h-4" />} label={t('sidebar.offer')} onClick={onClose} active={pathname.startsWith('/offer')} />}
            {isEn && <NavItem href={termsPageUrl} icon={<ScrollText className="w-4 h-4" />} label={t('sidebar.terms')} onClick={onClose} active={pathname.startsWith('/terms')} />}
            <NavItem href={paymentPageUrl} icon={<CreditCard className="w-4 h-4" />} label={t('footer.payment')} onClick={onClose} active={pathname.startsWith('/payment')} />
            <NavItem href={privacyPageUrl} icon={<Shield className="w-4 h-4" />} label={t('sidebar.privacy')} onClick={onClose} active={pathname.startsWith('/privacy')} />
            <NavItem href={refundPageUrl} icon={<RefreshCcw className="w-4 h-4" />} label={t('sidebar.refund')} onClick={onClose} active={pathname.startsWith('/refund')} />
          </nav>
        </div>
        <div className="p-4">
          {StatsContent}
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
            <a href={oldBasePageUrl} onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600 transition-colors">
              {t('sidebar.oldNoteGenerator')}
            </a>
            <LanguageSwitcher />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
